import React, { useRef, useState } from 'react';
import { Modal, Typography, Button } from '@crisellevaldez/speculo-ui';
import { Organization } from '../../../types/organization.types';
import { XCircle } from 'lucide-react';
import { useFollowUpBossOperations } from '../../../hooks/followupboss/useFollowUpBossOperations';
import { ConfirmationDialog } from '../../common/dialogs/ConfirmationDialog';
import {
  FubApiKeySetup,
  FubCustomFieldsSetup,
  FubStagesConfiguration,
  FubTeamMembersSync,
  FubWebhooksSetup,
  FubSubdomainSetup,
  FubAccordionStep,
} from './followUpBoss';
import { useFubAccordionStore } from './followUpBoss/store';

interface FollowUpBossModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrg?: Organization;
  onSuccess?: () => void;
}

export const FollowUpBossModal: React.FC<FollowUpBossModalProps> = ({
  isOpen,
  onClose,
  selectedOrg,
  onSuccess,
}) => {
  // Get operations from the hook
  const {
    // API key operations
    storeApiKey,
    isStoringApiKey,

    // Custom fields operations
    setupFubCustomFields,
    getFubCustomFields,
    validateFubCustomFields,
    isSettingUpCustomFields,
    isFetchingCustomFields,
    isValidatingCustomFields,

    // Stages operations
    pullFubStages,
    getFubStageAllowlist,
    setFubStageAllowlist,

    // Team members operations
    syncFubTeamMembers,
    isSyncingTeamMembers,

    // Webhook operations
    registerFubWebhooks,
    getFubWebhookStatus,
    isRegisteringWebhooks,
    isFetchingWebhookStatus,

    // Subdomain operations
    saveFubSubdomain,
    isSavingSubdomain,

    // Disconnect operations
    disconnectFub,
    isDisconnecting,
  } = useFollowUpBossOperations();

  // Get state and actions from Zustand store
  const {
    openStep,
    toggleStep,
    completeStep,
    resetForNewOrg,
    isConfigured: getIsConfigured,
    hasCustomFields: getHasCustomFields,
    hasStagesAllowlist: getHasStagesAllowlist,
    hasTeamMembersSync: getHasTeamMembersSync,
    hasWebhooksSetup: getHasWebhooksSetup,
    hasSubdomain: getHasSubdomain,
    completedSteps: getCompletedSteps,
  } = useFubAccordionStore();

  // Use a ref to track if we've already called resetForNewOrg for this organization
  const orgIdRef = useRef<string | null>(null);

  // State for confirmation dialog
  const [showConfirmDisconnect, setShowConfirmDisconnect] = useState(false);

  // Reset state when organization changes, but only if the organization ID has changed
  if (selectedOrg && selectedOrg.id !== orgIdRef.current) {
    orgIdRef.current = selectedOrg.id;
    resetForNewOrg(selectedOrg);
    setShowConfirmDisconnect(false);
  }

  // Compute derived values based on the organization
  const isConfigured = getIsConfigured(selectedOrg);
  const hasCustomFields = getHasCustomFields(selectedOrg);
  const hasStagesAllowlist = getHasStagesAllowlist(selectedOrg);
  const hasTeamMembersSync = getHasTeamMembersSync(selectedOrg);
  const hasWebhooksSetup = getHasWebhooksSetup(selectedOrg);
  const hasSubdomain = getHasSubdomain(selectedOrg);
  const completedSteps = getCompletedSteps(selectedOrg);

  // Handle step completion with success callback
  const handleStepComplete = (step: number) => {
    completeStep(step);

    // Call onSuccess to refresh parent data
    if (onSuccess) {
      onSuccess();
    }
  };

  // Handle modal close
  const handleClose = () => {
    setShowConfirmDisconnect(false);
    onClose();
  };

  // Handle disconnect confirmation
  const handleDisconnect = async () => {
    if (!selectedOrg?.id) return;

    const success = await disconnectFub(selectedOrg.id);
    if (success) {
      // Close the confirmation dialog
      setShowConfirmDisconnect(false);
      // Reset the accordion state
      resetForNewOrg(selectedOrg);
      // Call onSuccess to refresh parent data
      if (onSuccess) {
        onSuccess();
      }
      // Close the modal
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>
        <Typography variant="h3">Follow Up Boss Integration</Typography>
      </Modal.Header>
      <Modal.Body>
        <Typography variant="p" className="text-gray-600 dark:text-gray-400 mb-4">
          Connect your Follow Up Boss account to sync contacts and activities.
        </Typography>

        {/* Step 1: API Key Setup */}
        <FubAccordionStep
          stepNumber={1}
          title="API Key Setup"
          isOpen={openStep === 1}
          isCompleted={completedSteps[1]}
          canOpen={true}
          onToggle={() => toggleStep(1, openStep)}
        >
          <FubApiKeySetup
            selectedOrg={selectedOrg}
            isConfigured={isConfigured}
            onApiKeySaved={() => handleStepComplete(1)}
            storeApiKey={storeApiKey}
            isStoringApiKey={isStoringApiKey}
          />
        </FubAccordionStep>

        {/* Step 2: Custom Fields Setup */}
        <FubAccordionStep
          stepNumber={2}
          title="Custom Fields Setup"
          isOpen={openStep === 2}
          isCompleted={completedSteps[2]}
          canOpen={completedSteps[1]}
          onToggle={() => toggleStep(2, openStep)}
        >
          <FubCustomFieldsSetup
            selectedOrg={selectedOrg}
            isConfigured={isConfigured}
            hasCustomFields={hasCustomFields}
            onCustomFieldsSetup={() => handleStepComplete(2)}
            setupFubCustomFields={setupFubCustomFields}
            getFubCustomFields={getFubCustomFields}
            validateFubCustomFields={validateFubCustomFields}
            isSettingUpCustomFields={isSettingUpCustomFields}
            isFetchingCustomFields={isFetchingCustomFields}
            isValidatingCustomFields={isValidatingCustomFields}
          />
        </FubAccordionStep>

        {/* Step 3: Stages Configuration */}
        <FubAccordionStep
          stepNumber={3}
          title="Stages Configuration"
          isOpen={openStep === 3}
          isCompleted={completedSteps[3]}
          canOpen={completedSteps[2]}
          onToggle={() => toggleStep(3, openStep)}
        >
          <FubStagesConfiguration
            selectedOrg={selectedOrg}
            isConfigured={isConfigured}
            hasCustomFields={hasCustomFields}
            onStagesConfigured={() => handleStepComplete(3)}
            pullFubStages={pullFubStages}
            getFubStageAllowlist={getFubStageAllowlist}
            setFubStageAllowlist={setFubStageAllowlist}
            isOpen={openStep === 3}
          />
        </FubAccordionStep>

        {/* Step 4: Team Members Sync */}
        <FubAccordionStep
          stepNumber={4}
          title="Team Members Sync"
          isOpen={openStep === 4}
          isCompleted={completedSteps[4]}
          canOpen={completedSteps[3]}
          onToggle={() => toggleStep(4, openStep)}
        >
          <FubTeamMembersSync
            selectedOrg={selectedOrg}
            isConfigured={isConfigured}
            hasCustomFields={hasCustomFields}
            hasStagesAllowlist={hasStagesAllowlist}
            syncFubTeamMembers={syncFubTeamMembers}
            isSyncingTeamMembers={isSyncingTeamMembers}
            onTeamMembersSync={() => {
              handleStepComplete(4);
            }}
            isOpen={openStep === 4}
          />
        </FubAccordionStep>

        {/* Step 5: Webhooks Setup */}
        <FubAccordionStep
          stepNumber={5}
          title="Webhooks Setup"
          isOpen={openStep === 5}
          isCompleted={completedSteps[5]}
          canOpen={completedSteps[4]}
          onToggle={() => toggleStep(5, openStep)}
        >
          <FubWebhooksSetup
            selectedOrg={selectedOrg}
            isConfigured={isConfigured}
            hasCustomFields={hasCustomFields}
            hasStagesAllowlist={hasStagesAllowlist}
            hasTeamMembersSync={hasTeamMembersSync}
            registerFubWebhooks={registerFubWebhooks}
            getFubWebhookStatus={getFubWebhookStatus}
            isRegisteringWebhooks={isRegisteringWebhooks}
            isFetchingWebhookStatus={isFetchingWebhookStatus}
            onWebhooksSetup={() => {
              handleStepComplete(5);
            }}
            isOpen={openStep === 5}
          />
        </FubAccordionStep>

        {/* Step 6: Subdomain Setup */}
        <FubAccordionStep
          stepNumber={6}
          title="Subdomain Setup"
          isOpen={openStep === 6}
          isCompleted={completedSteps[6]}
          canOpen={completedSteps[5]}
          onToggle={() => toggleStep(6, openStep)}
        >
          <FubSubdomainSetup
            selectedOrg={selectedOrg}
            saveFubSubdomain={saveFubSubdomain}
            isSavingSubdomain={isSavingSubdomain}
            onSubdomainSaved={() => {
              handleStepComplete(6);
            }}
          />
        </FubAccordionStep>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex items-center justify-between w-full">
          <div>
            {completedSteps[1] && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowConfirmDisconnect(true)}
                className="flex items-center gap-2 text-red-600 hover:text-red-700 border-red-600 h-8"
                disabled={isDisconnecting}
              >
                <XCircle className="w-4 h-4" />
                Disconnect Sync
              </Button>
            )}
          </div>
          <Button variant="outline" onClick={handleClose} type="button">
            Close
          </Button>
        </div>
      </Modal.Footer>

      {/* Disconnect Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmDisconnect}
        onClose={() => setShowConfirmDisconnect(false)}
        onConfirm={handleDisconnect}
        title="Disconnect Follow Up Boss"
        message="Are you sure you want to disconnect Follow Up Boss integration? This will clear all FUB configurations, remove the stored API key, and reset the CRM connection."
        confirmLabel="Yes, Disconnect"
        cancelLabel="Cancel"
        variant="danger"
        isLoading={isDisconnecting}
      />
    </Modal>
  );
};
